import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';

const PREVIEW_WIDTH = 1280;
const PREVIEW_HEIGHT = 800;
const PREVIEW_WINDOW = 320;

type HostingTone = 'live' | 'offline' | 'inactive';

function siteHref(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function siteLabel(href: string): string {
  return href.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/**
 * Hosting the customer actually sees:
 * live while the subscription is paid and Vercel is serving the site,
 * not live when payment or a pause took it offline,
 * not active when hosting was never turned on.
 */
function describeHosting(
  status: string | null | undefined,
  paused: boolean | null | undefined,
): { tone: HostingTone; label: string; detail: string } {
  const s = (status || '').trim().toLowerCase();
  const paidUp = s === 'active' || s === 'trialing';

  if (paidUp && paused !== true) {
    return {
      tone: 'live',
      label: 'Live',
      detail:
        s === 'trialing'
          ? 'Hosting is in a trial and the website is online.'
          : 'Hosting is active and the website is online.',
    };
  }

  if (s === 'past_due' || s === 'unpaid') {
    return {
      tone: 'offline',
      label: 'Not live',
      detail: 'The website is offline because the hosting payment needs attention.',
    };
  }

  if (s === 'canceled' || s === 'cancelled') {
    return {
      tone: 'offline',
      label: 'Not live',
      detail: 'Hosting was canceled, so the website is not online.',
    };
  }

  if (paused === true && paidUp) {
    return {
      tone: 'offline',
      label: 'Not live',
      detail: 'The website is paused and is not being served.',
    };
  }

  return {
    tone: 'inactive',
    label: 'Not active',
    detail: 'Website hosting has not been activated, so the site is not online.',
  };
}

const TONE_CLASS: Record<HostingTone, string> = {
  live: 'bg-emerald-100 text-emerald-800',
  offline: 'bg-amber-100 text-amber-800',
  inactive: 'bg-gray-100 text-gray-600',
};

function HomepagePreview({ url }: { url: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.4);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const update = () => setScale(Math.max(el.clientWidth, 1) / PREVIEW_WIDTH);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={frameRef}
      className="relative w-full overflow-hidden bg-gray-100"
      style={{ height: PREVIEW_WINDOW }}
    >
      <iframe
        title="Website homepage"
        src={url}
        className="absolute top-0 left-0 border-0 bg-white pointer-events-none"
        style={{
          width: PREVIEW_WIDTH,
          height: PREVIEW_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

/**
 * Website page: a view of the Vercel site connected to this business
 * (linked from super admin), plus whether hosting is live.
 */
export default function Websites() {
  const { selectedBusiness } = useBusiness();

  if (!selectedBusiness) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center text-gray-500">
        Select a business to view its website.
      </div>
    );
  }

  const websiteUrl = (selectedBusiness.nexus_website_url || '').trim();
  const vercelProjectId = (selectedBusiness.vercel_project_id || '').trim();
  const connected = Boolean(vercelProjectId || websiteUrl);
  const href = siteHref(websiteUrl);
  const hosting = describeHosting(
    selectedBusiness.nexus_hosting_subscription_status,
    selectedBusiness.vercel_hosting_paused,
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Globe className="h-6 w-6 text-blue-600" /> Website
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {connected
            ? 'The website connected to this business, and whether hosting is live.'
            : 'A connected website for this business will show up here.'}
        </p>
      </div>

      {connected ? (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          {href ? (
            <HomepagePreview url={href} />
          ) : (
            <div className="h-[220px] flex flex-col items-center justify-center gap-2 bg-gray-50 text-gray-400">
              <Globe className="h-10 w-10" />
              <p className="text-sm">No public address yet</p>
            </div>
          )}

          <div className="p-5 md:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Hosting</p>
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${TONE_CLASS[hosting.tone]}`}
                  >
                    {hosting.label}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-2">{hosting.detail}</p>
              </div>
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 shrink-0 self-start"
                >
                  <ExternalLink className="h-4 w-4" /> Open website
                </a>
              ) : null}
            </div>

            {href ? (
              <p className="text-sm text-gray-500 break-all">
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {siteLabel(href)}
                </a>
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                This business has a website connected, but a public address has not been set yet.
              </p>
            )}

            {href ? (
              <p className="text-xs text-gray-400">
                The preview shows the top of the homepage. If it stays blank, the site may block embedding — use Open website.
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-gray-100 flex items-center justify-center">
            <Globe className="h-6 w-6 text-gray-400" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">No website connected</h2>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            When a website is connected to {(selectedBusiness.name || 'this business').trim()}, its homepage and hosting status will show here.
          </p>
        </div>
      )}
    </div>
  );
}
